import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { notificationsApi } from '@/api';
import {
  notificationPanelTitle,
  resolveNotificationModuleParam,
} from '@/config/notificationModuleScope';
import { useActiveModule } from '@/hooks/useActiveModule';
import type { NotificationResponse } from '@/types/notification';
import { showApiError } from '@/utils/apiError';

function isUnread(n: NotificationResponse): boolean {
  return n.readFlag !== true;
}

export function NotificationDropdown() {
  const location = useLocation();
  const activeModule = useActiveModule();
  const moduleParam = resolveNotificationModuleParam(location.pathname, activeModule);

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationResponse[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelTitle = notificationPanelTitle(moduleParam);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [page, countRes] = await Promise.all([
        notificationsApi.list({ page: 0, size: 10, module: moduleParam }),
        notificationsApi.unreadCount(moduleParam),
      ]);
      setItems(page.content);
      setUnread(countRes.count);
    } catch {
      /* notifications optional for some roles */
    } finally {
      setLoading(false);
    }
  }, [moduleParam]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const markRead = async (id: number) => {
    try {
      await notificationsApi.markRead(id);
      load();
    } catch (err) {
      showApiError(err, 'Failed to mark notification read');
    }
  };

  const markAllRead = async () => {
    try {
      await notificationsApi.markAllRead(moduleParam);
      load();
    } catch (err) {
      showApiError(err, 'Failed to mark all read');
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) load();
        }}
        title={panelTitle}
      >
        <NotificationsIcon fontSize="small" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
          <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
            <span className="text-sm font-semibold">{panelTitle}</span>
            {unread > 0 && (
              <button type="button" className="text-xs text-primary-600" onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {loading && <p className="p-3 text-sm text-slate-500">Loading…</p>}
            {!loading && items.length === 0 && (
              <p className="p-3 text-sm text-slate-500">
                {moduleParam ? 'No notifications for this module' : 'No notifications'}
              </p>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                type="button"
                className={`block w-full border-b border-slate-100 px-3 py-2 text-left hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800 ${
                  isUnread(n) ? 'bg-primary-50/50 dark:bg-primary-950/30' : ''
                }`}
                onClick={() => isUnread(n) && markRead(n.id)}
              >
                <p className="text-sm font-medium">{n.title}</p>
                <p className="text-xs text-slate-500 line-clamp-2">{n.message}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
